import { PersistentErrorBanner } from "@/components/persistent-error-banner";
import { useTranslation } from "react-i18next";

/**
 * Shell-level banner for a bootstrap read the app could not complete.
 *
 * OCT-11301: `OnboardingGate` used to read `featuresData?.planChosen ?? false`,
 * which cannot tell "this workspace has not chosen a plan" apart from "that
 * fetch errored". A throttled `/api/workspace/features` therefore made an
 * already-onboarded, paying workspace look plan-less and swapped the whole app
 * out from under the user. The gate now treats an errored bootstrap read as
 * UNKNOWN and keeps the user in the app they were already using - which means
 * something has to say out loud that the shell is running on defaults, and
 * offer the way back. That is this banner.
 *
 * Deliberately non-blocking: the user's data is unaffected and every list on
 * the page still works. Only plan-derived surfaces (feature gates, tier copy)
 * may be showing conservative defaults until the read succeeds. The retry
 * refetches ONLY the two bootstrap reads, never the whole cache - refetching
 * everything is what walked the user straight back into the throttle.
 */
export function BootstrapDegradedBanner(
  { onRetry, retryPending }: { onRetry: () => void; retryPending: boolean; },
) {
  const { t } = useTranslation("common");

  return (
    <PersistentErrorBanner
      title={t("bootstrapDegraded.title")}
      description={t("bootstrapDegraded.description")}
      onRetry={onRetry}
      retryPending={retryPending}
      retryLabel={t("bootstrapDegraded.retry")}
      className="mb-2"
    />
  );
}
